<template>
  <v-dialog
    max-width="720px"
    v-model="shouldShowDialog"
    @click:outside="close"
  >
    <v-card>
      <v-card-title v-if="targetChild">
        <div>{{ targetChild.lastName }} {{ targetChild.firstName }} - {{ editingData?.time }}</div>
        <v-spacer></v-spacer>
        <div>
          <v-btn
            color="red"
            variant="outlined"
            :disabled="isDeleting"
            @click="deleteData"
          >
            {{ $t('common.delete') }}
          </v-btn>
        </div>
      </v-card-title>

      <div class="pa-4">
        <div class="my-4" v-if="recorder">
          <h4 class="mb-2">{{ $t('nap.staffInCharge') }}: {{ recorder.lastName }} {{ recorder.firstName }}</h4>
        </div>

        <div class="my-4">
          <h4 class="mb-2">{{ $t('nap.postureChange') }}</h4>
          <v-select
            variant="outlined"
            density="compact"
            :items="poseOptions"
            item-value="value"
            item-title="label"
            v-model="pose"
          ></v-select>
        </div>

        <div class="my-4">
          <h4 class="mb-2">{{ $t('common.status') }}</h4>
          <div class="d-flex">
            <div
              v-for="s in statusOptions"
              :key="s.value"
              class="mx-2"
            >
              <v-btn
                :color="s.color"
                :variant="s.value === status ? 'flat' : 'outlined'"
                :theme="s.value === status ? 'dark' : 'light'"
                :value="s.value"
                @click="setStatus(s.value)"
              >
                <span v-if="s.isArrow">
                  <v-icon>{{ s.label }}</v-icon>
                </span>
                <span v-else>{{ s.label }}</span>
              </v-btn>
            </div>
            <div class="mx-2"></div>
          </div>
        </div>

        <div class="my-4">
          <h4 class="mb-2">{{ $t('nap.remarks') }}</h4>
          <v-select
            variant="outlined"
            density="compact"
            multiple
            :items="noteOptions"
            item-value="value"
            item-title="label"
            v-model="additionalNote"
          ></v-select>
        </div>

        <div class="my-4">
          <h4 class="mb-2">{{ $t('nap.comment') }}</h4>
          <v-text-field
            variant="outlined"
            density="compact"
            hide-details
            v-model="textComment"
          ></v-text-field>
        </div>

        <v-progress-linear
          v-if="isSaving"
          indeterminate
          color="primary"
          class="my-8"
        ></v-progress-linear>
        <v-progress-linear
          v-if="isDeleting"
          indeterminate
          color="red"
          class="my-8"
        ></v-progress-linear>
      </div>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          variant="flat"
          size="x-large"
          :disabled="isSaving"
          @click="save"
        >{{ $t('common.save') }}</v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import {
  napDetailModule,
  NapDetailData,
  poseOptions,
  PoseType,
  NapDataNote,
  additionalNoteOptions,
  NapDataStatus,
  napDataStatusOptions
} from '@/store/dataModules/napDetailModule';

import { NurseryChildResponse, NurseryNapIndividualOverview } from 'chaild-api/lib';
import { NurseryUserResponse } from 'chaild-api/src/component/nursery/user/types';
import dayjs from 'dayjs';
import { nurseryUserModule } from '@/store/dataModules/nurseryUserModule';

interface DataType {
  isDeleting: boolean;
  isSaving: boolean;
}

export default defineComponent({
  data: (): DataType => {
    return {
      isDeleting: false,
      isSaving: false,
    }
  },

  computed: {
    nap() {
      return napDetailModule.nap;
    },
    editingData() {
      return napDetailModule.editingNapDetailData;
    },
    // ⭕️ 修正4: shouldShowDialog に修正し、v-model用に getter/setter 化
    shouldShowDialog: {
      get(): boolean {
        return !!napDetailModule.editingNapDetailData;
      },
      set(val: boolean) {
        if (!val) {
          napDetailModule.setEditingNapDetailData(null);
        }
      }
    },
    targetChild(): NurseryChildResponse | null {
      if (this.nap && this.editingData) {
        const child = this.nap.children.find((c) => c.childId === this.editingData!.childId);
        if (child) {
          return child;
        }
      }
      return null;
    },
    staffOptions() {
      return napDetailModule.staffs.map((staff) => {
        return {
          userId: staff.userId,
          name: `${staff.lastName} ${staff.firstName}`
        }
      });
    },
    staff: {
      get(): number | null {
        if (this.editingData) {
          return this.editingData.staffId;
        }
        return null;
      },
      set(value: number) {
        if (this.editingData) {
          const data = {...this.editingData};
          data.staffId = value;
          napDetailModule.setEditingNapDetailData(data);
        }
      }
    },
    recorder(): NurseryUserResponse | null {
      if (this.nap) {
        const find = nurseryUserModule.staffs.find((staff) => staff.userId === this.nap?.recorderId);
        if (find) {
          return find;
        }
      }
      return null;
    },
    poseOptions() {
      return [
        { value: null, label: 'No change' },
        { value: poseOptions.leftToUp, label: 'Left-facing to face-up' },
        { value: poseOptions.downToUp, label: 'Prone to face-up' },
        { value: poseOptions.rightToUp, label: 'Right-facing to face-up' },
      ];
    },
    pose: {
      get(): PoseType | null {
        if (this.editingData) {
          return this.editingData.posturalChanged || null;
        }
        return null;
      },
      set(value: PoseType | null) {
        if (this.editingData) {
          const data = {...this.editingData};
          data.posturalChanged = value;
          napDetailModule.setEditingNapDetailData(data);
        }
      }
    },
    statusOptions() {
      if (this.editingData) {
        const find = napDetailModule.napData.find((data) => data.child.childId === this.editingData?.childId);
        if (find) {
          const divTimeStr = this.editingData.time as string;
          const divTime = parseInt(divTimeStr.replace(':', '')); 
          const overviews = find.overviews.filter((ov) => {
            const int1 = parseInt(ov.time);
            return int1 < divTime;
          });

          const latestStatus = this.getLatestStatus(overviews);
          if (latestStatus === 'getUp') {
            return [
              { value: napDataStatusOptions.sleepIn, label: 'Sleeping', color: 'primary' },
              { value: napDataStatusOptions.hug, label: 'Holding sleep', color: 'primary' },
            ];
          }
        }
      }

      return [
        { value: napDataStatusOptions.getUp, label: 'Wake up', color: '#EE6190' },
        { value: napDataStatusOptions.sleepIn, label: 'Sleeping', color: 'primary' },
        { value: napDataStatusOptions.hug, label: 'Holding sleep', color: 'primary' },
        { value: napDataStatusOptions.faceUp, label: 'mdi-arrow-up', color: '#02C07C', isArrow: true },
        { value: napDataStatusOptions.faceDown, label: 'mdi-arrow-down', color: '#02C07C', isArrow: true },
        { value: napDataStatusOptions.faceRight, label: 'mdi-arrow-right', color: '#02C07C', isArrow: true },
        { value: napDataStatusOptions.faceLeft, label: 'mdi-arrow-left', color: '#02C07C', isArrow: true },
      ];
    },
    status: {
      get(): NapDataStatus | null {
        if (this.editingData) {
          return this.editingData.status || null;
        }
        return null;
      },
      set(value: NapDataStatus | null) {
        if (this.editingData) {
          const data = {...this.editingData};
          data.status = value;
          napDetailModule.setEditingNapDetailData(data);
        }
      }
    },
    noteOptions() {
      return [
        { value: additionalNoteOptions.sneeze, label: 'Sneeze' },
        { value: additionalNoteOptions.cough, label: 'Cough' },
        { value: additionalNoteOptions.fever, label: 'Fever' },
        { value: additionalNoteOptions.sweat, label: 'Sweating' },
        { value: additionalNoteOptions.hiccup, label: 'Hiccups' },
        { value: additionalNoteOptions.runnyNose, label: 'Runny nose' },
        { value: additionalNoteOptions.breathDisorder, label: 'Irregular breathing' },
        { value: additionalNoteOptions.vomiting, label: 'Vomiting' },
      ];
    },
    additionalNote: {
      get(): NapDataNote[] {
        if (this.editingData) {
          return this.editingData.additionalNote || [];
        }
        return [];
      },
      set(value: NapDataNote[]) {
        if (this.editingData) {
          const data = {...this.editingData};
          data.additionalNote = value;
          napDetailModule.setEditingNapDetailData(data);
        }
      }
    },
    textComment: {
      get(): string | null {
        if (this.editingData) {
          return this.editingData.textComment || null;
        }
        return null;
      },
      set(value: string | null) {
        if (this.editingData) {
          const data = {...this.editingData};
          data.textComment = value;
          napDetailModule.setEditingNapDetailData(data);
        }
      }
    },
  },

  methods: {
    close() {
      napDetailModule.setEditingNapDetailData(null);
    },
    setStatus(value: NapDataStatus) {
      if (this.editingData) {
        const data = {...this.editingData};
        data.status = value;
        napDetailModule.setEditingNapDetailData(data);
      }
    },
    getLatestStatus(overview: NurseryNapIndividualOverview[]) {
      if (overview.length === 0) {
        return 'none';
      }
      const sorted = [...overview].sort((o1, o2) => {
        const time1 = dayjs(o1.time);
        const time2 = dayjs(o2.time);
        return time2.diff(time1);
      });
      return sorted[0].status;
    },
    async save() {
      this.isSaving = true;
      await napDetailModule.editData();
      this.isSaving = false;
    },
    async deleteData() {
      this.isDeleting = true;
      await napDetailModule.deleteData();
      this.isDeleting = false;
    }
  },
})
</script>